<template>
  <v-off-canvas
    id="resource-filters-offcanvas"
    :backdrop-static="false"
    offcanvas-class="offcanvas-end"
    @closed="$emit('closed')"
  >
    <resizable-container>
      <off-canvas-header>
      <h5 class="offcanvas-title">{{ $t('general.shared.filters') }}</h5>
      </off-canvas-header>

      <off-canvas-body>
      <v-form
        id="resource-filter-status-select"
        :label="this.$t('general.shared.status')"
        form-type="outline"
      >
        <v-select
          v-model="filterStatus"
          :clearable="false"
          :options="statusTypesOptions"
          :reduce="returnValue => returnValue.key"
          @option:selected="$emit('update:status', filterStatus); $emit('selected')"
          @option:deselected="$emit('update:status', filterStatus); $emit('selected')"
        >
          <template v-slot:no-options>
            <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
          </template>
        </v-select>
      </v-form>

      <v-form
        id="resource-filter-type-select"
        :label="this.$t('general.shared.type')"
        form-type="outline"
      >
        <v-select
          v-model="filterType"
          :clearable="false"
          :options="resourceTypeOptions"
          :reduce="returnValue => returnValue.key"
          @option:selected="$emit('update:type', filterType); $emit('selected')"
          @option:deselected="$emit('update:type', filterType); $emit('selected')"
        >
          <template v-slot:no-options>
            <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
          </template>
        </v-select>
      </v-form>

      <v-form
        id="resource-filter-types-select"
        :label="this.$t('general.shared.types')"
        form-type="outline"
      >
        <v-select
          v-model="filterTypes"
          :clearable="false"
          :options="resourceTypesOptions"
          :reduce="returnValue => returnValue.key"
          multiple
          @option:selected="$emit('update:types', filterTypes); $emit('selected')"
          @option:deselected="$emit('update:types', filterTypes); $emit('selected')"
        >
          <template v-slot:no-options>
            <div class="my-4">{{ $t('general.vueSelect.noOptions') }}</div>
          </template>
        </v-select>
      </v-form>

      <v-form-input
        id="resource-filter-location-select"
        v-model="filterLocation"
        :label="this.$t('general.shared.location')"
        autocomplete="off"
        form-type="outline"
        @update:modelValue="$emit('update:location', filterLocation); $emit('selected')"
      />
      </off-canvas-body>
    </resizable-container>  
  </v-off-canvas>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import vSelect from 'vue-select';
import { VForm, VFormInput } from '@uniqoders/form';
import VOffCanvas from '@/components/vendor/basic/offcanvas/VOffCanvas.vue';
import OffCanvasHeader from '@/components/vendor/basic/offcanvas/OffCanvasHeader.vue';
import OffCanvasBody from '@/components/vendor/basic/offcanvas/OffCanvasBody.vue';
import { mapConcepts } from '@/helpers/ConceptHelper';
import useConcepts from '@/helpers/Concepts';
import ResizableContainer from '@/components/resizablecontainer/ResizableContainer.vue';

export default defineComponent({
  name: 'TheResourceFiltersOffcanvas',
  components: {
    OffCanvasBody,
    OffCanvasHeader,
    VOffCanvas,
    vSelect,
    VForm,
    VFormInput,
    ResizableContainer,
  },
  emits: [
    'closed',
    'selected',
    'update:location',
    'update:status',
    'update:type',
    'update:types',
  ],
  props: {
    location: {
      type: String,
      required: false,
      default: '',
    },
    status: {
      type: String,
      required: true,
      default: 'active',
    },
    type: {
      type: String,
      required: false,
      default: '',
    },
    types: {
      type: Array,
      required: false,
    },
  },
  setup() {
    return {
      ...useConcepts(),
    };
  },
  data() {
    return {
      filterLocation: this.location,
      filterStatus: this.status,
      filterType: this.type,
      filterTypes: this.types,
    };
  },
  computed: {
    statusTypesOptions(): any {
      if (!!this.concepts && this.concepts['app.states']) {
        return mapConcepts(this.concepts['app.states']);
      }

      return [];
    },
    resourceTypeOptions(): any {
      if (!!this.concepts && this.concepts['resource.type']) {
        const sourceTypes = mapConcepts(this.concepts['resource.type']);

        sourceTypes.unshift({
          key: '',
          label: this.$t('general.shared.all'),
          permission: null,
          value: null,
          order: 0,
          status: 'active',
          icon: '',
          color: '',
        });

        return sourceTypes;
      }

      return [];
    },
    resourceTypesOptions(): any {
      if (!!this.concepts && this.concepts['resource.types']) {
        return mapConcepts(this.concepts['resource.types']);
      }

      return [];
    },
  },
  watch: {
    location(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterLocation = this.location;
      }
    },
    status(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterStatus = this.status;
      }
    },
    type(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterType = this.type;
      }
    },
    types(newValue, oldValue) {
      if (newValue !== oldValue) {
        this.filterTypes = this.types;
      }
    },
  },
});
</script>
